// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getShipment } from '@/shipment/api/get-shipment';
import { Shipment } from '@/shipment/api/types';
import { THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import {
  UseQueryResult,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';

/**
 * Cache key for single shipment queries.
 */
export const shipmentDetailQueryKey = (shipmentKey: string) => [
  'shipment',
  shipmentKey,
];

/**
 * React hook for fetching a single shipment by its key.
 *
 * @param shipmentKey - The unique key of the shipment to fetch
 * @param options - Optional query options
 * @returns Query result containing the shipment data
 *
 * @example
 * ```tsx
 * const { data: shipment, isLoading, error } = useShipment('SH-123');
 *
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error loading shipment</div>;
 * if (!shipment) return null;
 *
 * return <div>{shipment.shipmentNumber}</div>;
 * ```
 */
export function useShipment(
  shipmentKey: string | undefined,
  options?: { enabled?: boolean }
): UseQueryResult<Shipment, Error> {
  return useQuery({
    queryKey: shipmentDetailQueryKey(shipmentKey ?? ''),
    queryFn: () => {
      if (!shipmentKey) {
        throw new Error('Shipment key is required');
      }
      return getShipment(shipmentKey);
    },
    enabled: !!shipmentKey && (options?.enabled ?? true),
    staleTime: THIRTY_SECONDS_MS,
  });
}

/**
 * React hook for fetching a single shipment by key using Suspense.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Suspense mode for loading states
 * - Automatic refetch on window focus
 *
 * @param shipmentKey - The unique key of the shipment to fetch
 *
 * @returns Object containing:
 * - shipment: The shipment data
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useShipmentSuspense(shipmentKey: string) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: shipmentDetailQueryKey(shipmentKey),
    queryFn: () => getShipment(shipmentKey),
    refetchOnWindowFocus: true,
    refetchInterval: THIRTY_SECONDS_MS,
  });

  return { shipment: data, ...query };
}
